import '@fontsource/caveat'
import '@fontsource/dancing-script'
import '@fontsource/homemade-apple'

export interface TypedSignatureFont {
  id: string
  label: string
  family: string
}

/** Caveat and Dancing Script are OFL-1.1, Homemade Apple is Apache-2.0: all permit embedding in delivered PDFs. */
export const TYPED_SIGNATURE_FONTS: readonly TypedSignatureFont[] = [
  { id: 'caveat', label: 'Casual', family: 'Caveat' },
  { id: 'dancing-script', label: 'Flowing', family: 'Dancing Script' },
  { id: 'homemade-apple', label: 'Handwritten', family: 'Homemade Apple' },
]

export const getTypedSignatureFont = (id: string | undefined) =>
  TYPED_SIGNATURE_FONTS.find((font) => font.id === id) ??
  TYPED_SIGNATURE_FONTS[0]

/** Ink colour shared by the rasterized PNG and the on-screen preview. */
export const SIGNATURE_INK = '#0b1f4d'

const WIDTH = 800
const HEIGHT = 240
const FONT_SIZE = 96
const PADDING = 32

/**
 * Rasterizes typed text so it takes the same PNG embedding path as a drawn or
 * uploaded signature. Waits for the web font so the first render does not fall
 * back to a system face.
 */
export async function renderTypedSignatureDataUrl(
  text: string,
  font: TypedSignatureFont
): Promise<string> {
  const fontSpec = `${FONT_SIZE}px '${font.family}'`
  await document.fonts.load(fontSpec, text)

  const canvas = document.createElement('canvas')
  canvas.width = WIDTH
  canvas.height = HEIGHT
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas 2D context unavailable')

  ctx.fillStyle = SIGNATURE_INK
  ctx.textBaseline = 'middle'
  ctx.font = fontSpec
  const available = WIDTH - PADDING * 2
  const measured = ctx.measureText(text).width
  if (measured > available) {
    ctx.font = `${Math.floor((FONT_SIZE * available) / measured)}px '${font.family}'`
  }
  ctx.fillText(text, PADDING, HEIGHT / 2)
  return canvas.toDataURL('image/png')
}
